export type BillBundle = { label: string; detail: string; amount: number }

export type BillService = {
  name: string
  icon: string
  /** Billers a customer can pay for this category. */
  providers: string[]
  inputLabel: string
  inputHint: string
  /** Minimum digits before the reference is treated as complete. */
  inputDigits: number
  /** Whether the amount is free-entry or chosen from a published package. */
  mode: 'amount' | 'bundle'
  bundles?: BillBundle[]
  quickAmounts?: number[]
}

const AIRTIME_NETWORKS = ['Airtel', 'Glo', '9mobile']

export const BILL_SERVICES: BillService[] = [
  {
    name: 'Airtime',
    icon: 'airtime',
    providers: AIRTIME_NETWORKS,
    inputLabel: 'Phone number',
    inputHint: '0803 000 0000',
    inputDigits: 11,
    mode: 'amount',
    quickAmounts: [200, 500, 1000, 2000],
  },
  {
    name: 'Data',
    icon: 'data',
    providers: AIRTIME_NETWORKS,
    inputLabel: 'Phone number',
    inputHint: '0803 000 0000',
    inputDigits: 11,
    mode: 'bundle',
    bundles: [
      { label: '1.5GB · 30 days', detail: 'Everyday browsing', amount: 1200 },
      { label: '6GB · 30 days', detail: 'Streaming and calls', amount: 2500 },
      { label: '20GB · 30 days', detail: 'Heavy use', amount: 6000 },
      { label: '75GB · 30 days', detail: 'Household plan', amount: 18000 },
    ],
  },
  {
    name: 'Electricity',
    icon: 'electricity',
    providers: ['Ikeja Electric', 'Eko Electric', 'Abuja Electric', 'Port Harcourt Electric'],
    inputLabel: 'Meter number',
    inputHint: '12 digit meter number',
    inputDigits: 11,
    mode: 'amount',
    quickAmounts: [2000, 5000, 10000, 20000],
  },
  {
    name: 'Cable TV',
    icon: 'tv',
    providers: ['DStv', 'GOtv', 'StarTimes'],
    inputLabel: 'Smartcard number',
    inputHint: '10 digit smartcard number',
    inputDigits: 10,
    mode: 'bundle',
    bundles: [
      { label: 'Basic', detail: 'Local channels', amount: 3900 },
      { label: 'Family', detail: 'Local plus sport', amount: 7900 },
      { label: 'Premium', detail: 'Full channel list', amount: 24500 },
    ],
  },
  {
    name: 'Internet',
    icon: 'internet',
    providers: ['Spectranet', 'Smile', 'FiberOne'],
    inputLabel: 'Account or device ID',
    inputHint: '10 digit account ID',
    inputDigits: 10,
    mode: 'bundle',
    bundles: [
      { label: '40GB · 30 days', detail: 'Home starter', amount: 9500 },
      { label: '100GB · 30 days', detail: 'Home standard', amount: 17500 },
      { label: 'Unlimited · 30 days', detail: 'Work from home', amount: 32000 },
    ],
  },
  {
    name: 'Water',
    icon: 'water',
    providers: ['Lagos Water Corporation', 'Abuja Water Board', 'Rivers Water Board'],
    inputLabel: 'Customer number',
    inputHint: '10 digit customer number',
    inputDigits: 10,
    mode: 'amount',
    quickAmounts: [3000, 6000, 12000, 24000],
  },
  {
    name: 'Education',
    icon: 'education',
    providers: ['WAEC', 'NECO', 'JAMB'],
    inputLabel: 'Candidate number',
    inputHint: '10 digit candidate number',
    inputDigits: 10,
    mode: 'bundle',
    bundles: [
      { label: 'Result checker', detail: 'Single use pin', amount: 3500 },
      { label: 'Registration pin', detail: 'One candidate', amount: 6500 },
    ],
  },
  {
    name: 'Insurance',
    icon: 'insurance',
    providers: ['Leadway', 'AIICO', 'AXA Mansard'],
    inputLabel: 'Policy number',
    inputHint: '10 digit policy number',
    inputDigits: 10,
    mode: 'amount',
    quickAmounts: [5000, 12000, 25000, 60000],
  },
  {
    name: 'Betting',
    icon: 'betting',
    providers: ['SportyBet', 'Bet9ja', '1xBet'],
    inputLabel: 'Wallet or user ID',
    inputHint: 'Account user ID',
    inputDigits: 6,
    mode: 'amount',
    quickAmounts: [1000, 2000, 5000, 10000],
  },
]

/** Billers the customer has paid before, surfaced for one-tap repeat payments. */
export const SAVED_BILLERS = [
  { service: 'Airtime', provider: 'Airtel', target: '0803 123 4567', label: 'My line' },
  { service: 'Electricity', provider: 'Ikeja Electric', target: '4512 8890 3341', label: 'Home meter' },
  { service: 'Cable TV', provider: 'DStv', target: '7028 441 990', label: 'Living room' },
]

export function findService(name: string | null): BillService {
  return BILL_SERVICES.find((service) => service.name === name) || BILL_SERVICES[0]
}

export function serviceIcon(name: string | null) {
  return BILL_SERVICES.find((service) => service.name === name)?.icon || 'bills'
}
